import { getBrowserTimezone } from '~/features/shared/calendar/dateUtils';

interface DateTimeParts {
  day: number;
  hour: number;
  minute: number;
  month: number;
  year: number;
}

const parseClock = (clock: string): [number, number] => {
  const [hours, minutes] = clock.split(':').map(Number);
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    throw new Error('Enter a valid start and end time.');
  }
  return [hours, minutes];
};

const parseDate = (isoDate: string): [number, number, number] => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) throw new Error('Enter a valid availability date.');
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(0);
  candidate.setUTCFullYear(year, month - 1, day);
  candidate.setUTCHours(0, 0, 0, 0);
  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day
  ) {
    throw new Error('Enter a valid availability date.');
  }
  return [year, month, day];
};

const formatterFor = (timezone: string) =>
  new Intl.DateTimeFormat('en-US', {
    calendar: 'gregory',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    numberingSystem: 'latn',
    timeZone: timezone,
    year: 'numeric',
  });

const partsInTimezone = (timestamp: Date, timezone: string): DateTimeParts => {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = formatterFor(timezone).formatToParts(timestamp);
  } catch {
    throw new Error(`Invalid IANA timezone: ${timezone}.`);
  }
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
  };
};

const partsAsUtcMilliseconds = (parts: DateTimeParts): number =>
  Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);

/** Resolve one provider-local wall clock to an instant without using the browser zone. */
const zonedTimestamp = (isoDate: string, clock: string, timezone: string): Date => {
  const [year, month, day] = parseDate(isoDate);
  const [hour, minute] = parseClock(clock);
  const expected: DateTimeParts = { year, month, day, hour, minute };
  const expectedMilliseconds = partsAsUtcMilliseconds(expected);
  let candidateMilliseconds = expectedMilliseconds;

  // Iteratively correct the candidate by the difference between the desired
  // wall clock and what Intl renders in the target zone. This converges across
  // normal offset changes and deliberately fails for a spring-forward gap.
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const actual = partsInTimezone(new Date(candidateMilliseconds), timezone);
    const correction = expectedMilliseconds - partsAsUtcMilliseconds(actual);
    if (correction === 0) return new Date(candidateMilliseconds);
    candidateMilliseconds += correction;
  }

  const actual = partsInTimezone(new Date(candidateMilliseconds), timezone);
  if (partsAsUtcMilliseconds(actual) !== expectedMilliseconds) {
    throw new Error('That local time does not exist because of a daylight-saving transition.');
  }
  return new Date(candidateMilliseconds);
};

const nextISODate = (isoDate: string): string => {
  const [year, month, day] = parseDate(isoDate);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(
    next.getUTCDate()
  ).padStart(2, '0')}`;
};

const resolvedTimezone = (timezone?: string): string => {
  const resolved = timezone || getBrowserTimezone();
  if (!resolved) throw new Error('Your browser timezone could not be determined.');
  return resolved;
};

export const buildShiftTimestamps = (
  isoDate: string,
  startTime: string,
  endTime: string,
  timezone?: string
): { endsAt: string; startsAt: string } => {
  const targetTimezone = resolvedTimezone(timezone);
  const [startHours, startMinutes] = parseClock(startTime);
  const [endHours, endMinutes] = parseClock(endTime);
  const overnight = endHours * 60 + endMinutes <= startHours * 60 + startMinutes;
  const endDate = overnight ? nextISODate(isoDate) : isoDate;
  return {
    startsAt: zonedTimestamp(isoDate, startTime, targetTimezone).toISOString(),
    endsAt: zonedTimestamp(endDate, endTime, targetTimezone).toISOString(),
  };
};

export const timestampToLocalClock = (timestamp: string | null, timezone?: string): string => {
  if (!timestamp) return '';
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return '';
  const parts = partsInTimezone(parsed, resolvedTimezone(timezone));
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
};

export const timezoneAbbreviation = (timestamp: string, timezone: string): string => {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date(timestamp))
      .find((value) => value.type === 'timeZoneName');
    return part?.value ?? timezone;
  } catch {
    return timezone;
  }
};
